import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    AlertCircle, ArrowUpCircle, Banknote, Check, ChevronLeft, Clock, CreditCard, Crown,
    CheckCircle2, ChevronDown, ChevronUp, History, Loader2, Lock, PackageOpen, Plus, Power, RefreshCcw, Search, ShoppingBag, Smartphone, UserPlus, Wallet, X, Zap,
} from 'lucide-react';
import styles from './RenewLoyalty.module.css';
import { useStore } from '../../../../../context/StoreContext';
import useAccess from '../../../../../hooks/useAccess';
import { SMIIFY_API } from '../../../../../config/api';
import AddCustomerForm from '../../../../../components/AddCustomerForm/AddCustomerForm';

// =============================================================================
// Memberships desk: sell, recharge, renew / extend, upgrade.
// Backend: /api/v1/smiify/sm/memberships  (plans, wallet, quote, checkout, history)
// Every checkout creates a completed bill.
//
// Access (module "memberships", same rules as the server):
//   Sell new                   memberships.sell_membership
//   Recharge / renew / upgrade memberships.renew, memberships.sell_membership or billing.create
//   Activate discount plan     memberships.allot_cards, memberships.sell_membership or billing.create
//   Allot a card               memberships.allot_cards
//   Passbook / history         memberships.view_passbook or memberships.view
//   New client                 customers.create or billing.create
// =============================================================================

const CUSTOMER_API = `${SMIIFY_API}/customers`;
const MEMBERSHIP_API = `${SMIIFY_API}/memberships`;
const PREPAID_API = `${SMIIFY_API}/prepaid-memberships`;
const STAFF_API = `${SMIIFY_API}/staff`;

/* ----------------------------- helpers ----------------------------- */
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const isValidDate = (v) => v && !Number.isNaN(new Date(v).getTime());
const fmtDate = (v) => (isValidDate(v) ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (v) => (isValidDate(v)
    ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
    : '—');
const lastFour = (num) => {
    const digits = String(num || '').replace(/\s+/g, '');
    return digits ? digits.slice(-4) : '••••';
};
const validityText = (value, type) => {
    if (!value) return '';
    const t = String(type || '').toLowerCase();
    const unit = t.startsWith('y') ? 'year' : t.startsWith('d') ? 'day' : 'month';
    return `${value} ${unit}${Number(value) === 1 ? '' : 's'}`;
};
const newRequestId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const STATUS_LABEL = { ACTIVE: 'Active', ASSIGNED: 'Active', EXPIRED: 'Expired', INACTIVE: 'Not activated', REVOKED: 'Blocked' };

const ACTIONS = [
    { key: 'SELL', label: 'Sell new', hint: 'New prepaid card or discount membership', Icon: Plus },
    { key: 'RECHARGE', label: 'Recharge card', hint: 'Add balance to a prepaid card', Icon: Zap },
    { key: 'RENEW', label: 'Renew or extend', hint: 'Extend validity of an existing membership', Icon: RefreshCcw },
    { key: 'UPGRADE', label: 'Upgrade', hint: 'Move a discount membership to a higher plan', Icon: ArrowUpCircle },
];

const PAYMENT_MODES = [
    { key: 'UPI', label: 'UPI', Icon: Smartphone },
    { key: 'CARD', label: 'Card', Icon: CreditCard },
    { key: 'CASH', label: 'Cash', Icon: Banknote },
];

const TXN_LABEL = {
    SOLD: 'Bought', ISSUED: 'Card issued', ACTIVATED: 'Activated', RECHARGED: 'Recharged', RENEWED: 'Renewed',
    EXTENDED: 'Extended', UPGRADED: 'Upgraded', USED: 'Used for services', REFUNDED: 'Refunded to card', EXPIRED: 'Expired', ADJUSTED: 'Adjusted',
};

/* ============================== component ============================== */
export default function RenewLoyalty({ onBack }) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { activeStore } = useStore();
    const { can, canAny, isOwner } = useAccess();
    const storeId = activeStore?._id || activeStore?.id;
    const token = localStorage.getItem('smiify_token');
    const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId }), [token, storeId]);

    // Live access (the server checks the same rules)
    const canSell = isOwner || can('memberships.sell_membership');
    const canRenew = isOwner || canAny(['memberships.renew', 'memberships.sell_membership', 'billing.create']);
    const canActivate = isOwner || canAny(['memberships.allot_cards', 'memberships.sell_membership', 'billing.create']);
    const canAllot = isOwner || can('memberships.allot_cards');
    const canPassbook = isOwner || canAny(['memberships.view_passbook', 'memberships.view']);
    const canAddClient = isOwner || canAny(['customers.create', 'billing.create']);
    const actionAllowed = useCallback((key) => (key === 'SELL' ? canSell : canRenew), [canSell, canRenew]);
    // Buy a package again = a new bill with the package on it
    const canBuyPackage = isOwner || (can('billing.create') && can('packages.sell_package'));
    const visibleActions = ACTIONS.filter((a) => actionAllowed(a.key));

    const api = useCallback(async (path, options = {}) => {
        const res = await fetch(`${MEMBERSHIP_API}${path}`, { ...options, headers });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.success === false) throw new Error(json.message || `Request failed (${res.status}).`);
        return json;
    }, [headers]);

    // client
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [client, setClient] = useState(null);
    const [wallet, setWallet] = useState({ cards: [], pending: [], discounts: [], packages: [] });
    const [openPackage, setOpenPackage] = useState('');   // package whose history is open
    const [showFinished, setShowFinished] = useState(false); // used up / expired packages
    const [walletState, setWalletState] = useState({ loading: false, error: '' });
    const [showAddClient, setShowAddClient] = useState(false);
    const searchSeq = useRef(0);

    // plans
    const [plansData, setPlansData] = useState({ discount: [], prepaid: [], upgradePricing: 'PRORATA' });
    const [plansState, setPlansState] = useState({ loading: true, error: '' });

    // order
    const [action, setAction] = useState('');
    const [sellKind, setSellKind] = useState('PREPAID');
    const [targetId, setTargetId] = useState('');
    const [planId, setPlanId] = useState('');
    const [paymentMode, setPaymentMode] = useState('UPI');
    const [quote, setQuote] = useState(null);
    const [quoteState, setQuoteState] = useState({ loading: false, error: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [checkoutError, setCheckoutError] = useState('');
    const [doneOrder, setDoneOrder] = useState(null);
    const requestIdRef = useRef(newRequestId());

    // Staff who sold the membership (shows in staff analytics)
    const [staffList, setStaffList] = useState([]);
    const [soldById, setSoldById] = useState('');

    // GST (optional, staff chooses the rate)
    const [gstOn, setGstOn] = useState(true);
    const [gstRate, setGstRate] = useState(null); // null = store default

    // activation / allotment inside the desk
    const [busyId, setBusyId] = useState('');
    const [flash, setFlash] = useState(null); // { type: 'ok' | 'error', text }
    const [allotOpen, setAllotOpen] = useState({}); // pendingId -> { loading, cards, selected }
    const presetRef = useRef(null);

    // history drawer
    const [historyFor, setHistoryFor] = useState(null); // { title, params }
    const [historyRows, setHistoryRows] = useState([]);
    const [historyState, setHistoryState] = useState({ loading: false, error: '', nextBefore: null });

    /* ----------------------------- plans ----------------------------- */
    useEffect(() => {
        if (!storeId || !token) return;
        let alive = true;
        (async () => {
            setPlansState({ loading: true, error: '' });
            try {
                const json = await api('/plans');
                if (alive) {
                    setPlansData(json.data || { discount: [], prepaid: [] });
                    setPlansState({ loading: false, error: '' });
                }
            } catch (e) {
                if (alive) setPlansState({ loading: false, error: e.message });
            }
        })();
        return () => { alive = false; };
    }, [api, storeId, token]);

    /* ----------------------------- staff list (for "Sold by") ----------------------------- */
    useEffect(() => {
        if (!storeId || !token) return undefined;
        let alive = true;
        (async () => {
            try {
                const res = await fetch(`${STAFF_API}?storeId=${storeId}`, { headers });
                const json = await res.json().catch(() => ({}));
                const list = (json.data || [])
                    .filter((st) => !st.status || st.status === 'Active')
                    .map((st) => ({ id: String(st._id || st.id), name: st.name }));
                if (alive) setStaffList(list);
            } catch { /* optional field: ignore */ }
        })();
        return () => { alive = false; };
    }, [storeId, token, headers]);

    /* ----------------------------- client search (debounced) ----------------------------- */
    useEffect(() => {
        const q = query.trim();
        if (client || q.length < 2 || !storeId) {
            setResults([]);
            setIsSearching(false);
            return undefined;
        }
        const seq = ++searchSeq.current;
        const timer = setTimeout(async () => {
            setIsSearching(true);
            try {
                const res = await fetch(`${CUSTOMER_API}/search?query=${encodeURIComponent(q)}`, { headers });
                const json = await res.json().catch(() => ({}));
                if (seq === searchSeq.current) setResults(Array.isArray(json.data) ? json.data : []);
            } catch {
                if (seq === searchSeq.current) setResults([]);
            } finally {
                if (seq === searchSeq.current) setIsSearching(false);
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [query, client, storeId, headers]);

    /* ----------------------------- wallet ----------------------------- */
    const loadWallet = useCallback(async (customerId) => {
        setWalletState({ loading: true, error: '' });
        try {
            const json = await api(`/customers/${customerId}/wallet`);
            setWallet({ packages: [], ...(json.data || { cards: [], pending: [], discounts: [] }) });
            setWalletState({ loading: false, error: '' });
        } catch (e) {
            setWallet({ cards: [], pending: [], discounts: [] });
            setWalletState({ loading: false, error: e.message });
        }
    }, [api]);

    const resetOrder = () => {
        setAction('');
        setTargetId('');
        setPlanId('');
        setQuote(null);
        setQuoteState({ loading: false, error: '' });
        setCheckoutError('');
        setDoneOrder(null);
        requestIdRef.current = newRequestId();
    };

    const pickClient = (c) => {
        setFlash(null);
        setAllotOpen({});
        setClient(c);
        setQuery('');
        setResults([]);
        resetOrder();
        loadWallet(c._id || c.id);
    };

    /* ----------------------------- open with customer from URL ----------------------------- */
    // e.g. ?phone=9876543210&customerId=..&action=RENEW&kind=PREPAID&target=<cardId>
    useEffect(() => {
        const phone = (searchParams.get('phone') || '').replace(/\D/g, '').slice(-10);
        const customerId = searchParams.get('customerId') || '';
        if (!storeId || client || (!phone && !customerId)) return;

        presetRef.current = {
            action: (searchParams.get('action') || '').toUpperCase(),
            kind: (searchParams.get('kind') || '').toUpperCase(),
            target: searchParams.get('target') || '',
        };
        (async () => {
            try {
                const res = await fetch(`${CUSTOMER_API}/search?query=${encodeURIComponent(phone || customerId)}`, { headers });
                const json = await res.json().catch(() => ({}));
                const list = Array.isArray(json.data) ? json.data : [];
                const match = list.find((c) => String(c._id || c.id) === customerId) || list.find((c) => c.phone === phone) || list[0];
                if (match) pickClient(match);
                else setQuery(phone);
            } catch {
                setQuery(phone);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId]);

    /* ----------------------------- activate / allot inside the desk ----------------------------- */
    const customerKey = client ? (client._id || client.id) : '';

    const postCustomer = async (path, body) => {
        const res = await fetch(`${CUSTOMER_API}${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.success === false) throw new Error(json.message || `Request failed (${res.status}).`);
        return json;
    };

    const activateDiscount = async (membershipId) => {
        if (!canActivate) return;
        setBusyId(membershipId);
        setFlash(null);
        try {
            const json = await postCustomer('/activate-discount-membership', { customerId: customerKey, membershipId });
            setFlash({ type: 'ok', text: json.message || 'Membership activated.' });
            await loadWallet(customerKey);
        } catch (e) {
            setFlash({ type: 'error', text: e.message });
        } finally {
            setBusyId('');
        }
    };

    const openAllot = async (pending) => {
        if (!canAllot) return;
        setAllotOpen((m) => ({ ...m, [pending.id]: { loading: true, cards: [], selected: '' } }));
        try {
            const res = await fetch(`${PREPAID_API}/cards/available/${pending.templateId}`, { headers });
            const json = await res.json().catch(() => ({}));
            const cardsList = (json.data || []).filter((c) => !c.status || c.status === 'AVAILABLE');
            setAllotOpen((m) => ({ ...m, [pending.id]: { loading: false, cards: cardsList, selected: cardsList[0]?.cardNumber || '' } }));
        } catch {
            setAllotOpen((m) => ({ ...m, [pending.id]: { loading: false, cards: [], selected: '' } }));
        }
    };

    const allotCard = async (pendingId) => {
        const cardNumber = String(allotOpen[pendingId]?.selected || '').replace(/\s+/g, '');
        if (!cardNumber || !canAllot) return;
        setBusyId(pendingId);
        setFlash(null);
        try {
            const json = await postCustomer('/allot-card', { customerId: customerKey, membershipId: pendingId, cardNumber });
            setFlash({ type: 'ok', text: json.message || 'Card allotted.' });
            setAllotOpen((m) => { const next = { ...m }; delete next[pendingId]; return next; });
            await loadWallet(customerKey);
        } catch (e) {
            setFlash({ type: 'error', text: e.message });
        } finally {
            setBusyId('');
        }
    };

    /* ----------------------------- derived ----------------------------- */
    const { cards, pending, discounts } = wallet;
    const allPackages = Array.isArray(wallet.packages) ? wallet.packages : [];
    const finishedPackages = allPackages.filter((p) => !(p.status === 'ACTIVE' && Number(p.totalLeft) > 0));
    // Active packages with services left always show; finished ones only on request (for history / buy again)
    const packages = showFinished ? allPackages : allPackages.filter((p) => p.status === 'ACTIVE' && Number(p.totalLeft) > 0);
    const buyPackageAgain = (pkg) => {
        if (!client || !pkg?.templateId) return;
        const qs = new URLSearchParams({ package: pkg.templateId, phone: client.phone || '', customerId: client._id || client.id || '', name: client.name || '' });
        navigate(`/create-bill?${qs.toString()}`);
    };
    const discountPlans = plansData.discount || [];
    const prepaidPlans = plansData.prepaid || [];
    const activeDiscount = discounts.find((d) => d.status === 'ACTIVE');
    const waitingDiscount = discounts.find((d) => d.status === 'INACTIVE');
    const maxTier = discountPlans.length;

    const targetsFor = useCallback((key) => {
        if (key === 'RECHARGE') return cards.filter((c) => c.status === 'ASSIGNED');
        if (key === 'RENEW') {
            return [
                ...cards.filter((c) => ['ASSIGNED', 'EXPIRED'].includes(c.status)),
                ...discounts.filter((d) => ['ACTIVE', 'EXPIRED'].includes(d.status)),
            ];
        }
        if (key === 'UPGRADE') return discounts.filter((d) => ['ACTIVE', 'EXPIRED'].includes(d.status) && (d.tier || 0) < maxTier);
        return [];
    }, [cards, discounts, maxTier]);

    const actionDisabledReason = (key) => {
        if (!actionAllowed(key)) return 'No access';
        if (!client) return 'Select a client first';
        if (walletState.loading) return 'Loading memberships...';
        if (key === 'RECHARGE' && !targetsFor('RECHARGE').length) return cards.length ? 'No active card (renew an expired one)' : 'No prepaid card yet';
        if (key === 'RENEW' && !targetsFor('RENEW').length) return 'No membership to renew';
        if (key === 'UPGRADE' && !targetsFor('UPGRADE').length) return discounts.length ? 'Already on the highest plan' : 'No discount membership';
        return '';
    };

    const targets = targetsFor(action);
    const target = targets.find((t) => t.id === targetId) || null;
    const needsTarget = ['RECHARGE', 'RENEW', 'UPGRADE'].includes(action);
    const planKind = action === 'SELL' ? sellKind : action === 'RECHARGE' ? 'PREPAID' : target?.kind || '';

    // Preset from the bill screen: choose the action + card/membership automatically
    useEffect(() => {
        const preset = presetRef.current;
        if (!preset || !client || walletState.loading) return;
        presetRef.current = null;
        setSearchParams({}, { replace: true });
        if (!preset.action || !ACTIONS.some((a) => a.key === preset.action)) return;
        if (actionDisabledReason(preset.action)) return;
        setAction(preset.action);
        if (preset.action === 'SELL' && ['PREPAID', 'DISCOUNT'].includes(preset.kind)) setSellKind(preset.kind);
        if (preset.target && targetsFor(preset.action).some((t) => t.id === preset.target)) setTargetId(preset.target);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client, walletState.loading, cards.length, discounts.length]);

    // auto-pick when there is only one choice
    useEffect(() => {
        if (needsTarget && targets.length === 1 && targetId !== targets[0].id) setTargetId(targets[0].id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, targets.length]);

    const plans = useMemo(() => {
        if (!action) return [];
        if (planKind === 'PREPAID') return prepaidPlans;
        if (planKind !== 'DISCOUNT') return [];
        if (action === 'SELL') return discountPlans;
        if (action === 'UPGRADE') return discountPlans.filter((p) => (p.tier || 0) > (target?.tier || 0));
        if (action === 'RENEW') return discountPlans.filter((p) => p.id === target?.templateId);
        return [];
    }, [action, planKind, target, prepaidPlans, discountPlans]);

    // Renew: preselect the same plan (discount) or the card's own plan (prepaid)
    useEffect(() => {
        if (action !== 'RENEW' || !target || planId) return;
        const own = plans.find((p) => p.id === target.templateId);
        if (own) setPlanId(own.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, targetId, plans.length]);

    const plan = plans.find((p) => p.id === planId) || null;
    const sellDiscountBlocked = action === 'SELL' && sellKind === 'DISCOUNT' && Boolean(activeDiscount || waitingDiscount);
    const membershipType = action === 'SELL' ? sellKind : action === 'RECHARGE' ? 'PREPAID' : target?.kind;
    const readyForQuote = Boolean(client && action && actionAllowed(action) && plan && (!needsTarget || target) && !sellDiscountBlocked);

    /* ----------------------------- live quote ----------------------------- */
    useEffect(() => {
        setCheckoutError('');
        if (!readyForQuote) {
            setQuote(null);
            setQuoteState({ loading: false, error: '' });
            return undefined;
        }
        let alive = true;
        setQuoteState({ loading: true, error: '' });
        const timer = setTimeout(async () => {
            try {
                const json = await api('/quote', {
                    method: 'POST',
                    body: JSON.stringify({
                        action, membershipType, customerId: client._id || client.id,
                        planId: plan.id, targetId: needsTarget ? target.id : null,
                        gstRate: gstOn ? gstRate : 0,
                    }),
                });
                if (alive) {
                    setQuote(json.data);
                    setQuoteState({ loading: false, error: '' });
                }
            } catch (e) {
                if (alive) {
                    setQuote(null);
                    setQuoteState({ loading: false, error: e.message });
                }
            }
        }, 250);
        return () => { alive = false; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readyForQuote, action, membershipType, planId, targetId, client, gstOn, gstRate]);

    /* ----------------------------- checkout ----------------------------- */
    const handleCheckout = async () => {
        if (!quote || isSubmitting || !actionAllowed(action)) return;
        setIsSubmitting(true);
        setCheckoutError('');
        try {
            const json = await api('/checkout', {
                method: 'POST',
                body: JSON.stringify({
                    action, membershipType, customerId: client._id || client.id,
                    planId: plan.id, targetId: needsTarget ? target.id : null,
                    paymentMode, requestId: requestIdRef.current, expectedTotal: quote.total,
                    gstRate: gstOn ? gstRate : 0,
                    soldBy: soldById ? { staffId: soldById, name: staffList.find((st) => st.id === soldById)?.name || '' } : null,
                }),
            });
            const { invoice, wallet: nextWallet } = json.data || {};
            if (nextWallet) setWallet(nextWallet);
            setDoneOrder({
                invoiceId: invoice?.id,
                invoiceNo: invoice?.invoiceNo,
                total: invoice?.total ?? quote.total,
                planName: plan.name,
                actionLabel: ACTIONS.find((a) => a.key === action)?.label,
                clientName: client.name,
                paymentMode,
                pendingAllotment: action === 'SELL' && sellKind === 'PREPAID',
            });
        } catch (e) {
            setCheckoutError(e.message);
            // price changed etc. -> fetch a fresh quote
            if (/price changed/i.test(e.message)) setQuote(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ----------------------------- history ----------------------------- */
    const openHistory = async (title, params, append = false) => {
        if (!canPassbook) return;
        if (!append) {
            setHistoryFor({ title, params });
            setHistoryRows([]);
        }
        setHistoryState((s) => ({ ...s, loading: true, error: '' }));
        try {
            const qs = new URLSearchParams({ ...params, limit: '30' });
            if (append && historyState.nextBefore) qs.set('before', historyState.nextBefore);
            const json = await api(`/history?${qs.toString()}`);
            setHistoryRows((rows) => (append ? [...rows, ...(json.data || [])] : json.data || []));
            setHistoryState({ loading: false, error: '', nextBefore: json.nextBefore || null });
        } catch (e) {
            setHistoryState({ loading: false, error: e.message, nextBefore: null });
        }
    };

    const actionLabel = ACTIONS.find((a) => a.key === action)?.label || '';

    /* ============================== render ============================== */
    return (
        <div className={styles.workspace}>
            {showAddClient && canAddClient && (
                <AddCustomerForm
                    initialPhone={/^\d+$/.test(query) ? query : ''}
                    onClose={() => setShowAddClient(false)}
                    onSuccess={(c) => { setShowAddClient(false); pickClient(c); }}
                />
            )}

            <header className={styles.topBar}>
                <button type="button" className={styles.backBtn} onClick={onBack || (() => navigate(-1))} aria-label="Back">
                    <ChevronLeft size={16} />
                </button>
                <div>
                    <h1 className={styles.pageTitle}>Memberships</h1>
                    <p className={styles.pageSub}>Sell, recharge, renew or upgrade a client's membership</p>
                </div>
            </header>

            <div className={styles.layout}>
                {/* ======================= LEFT: steps ======================= */}
                <div className={styles.steps}>
                    {/* STEP 1: client */}
                    <section className={styles.step}>
                        <div className={styles.stepHead}>
                            <span className={`${styles.stepNo} ${client ? styles.stepDone : ''}`}>{client ? <Check size={13} /> : 1}</span>
                            <h2>Client</h2>
                        </div>

                        {!client ? (
                            <div>
                                <div className={styles.searchBox}>
                                    {isSearching ? <Loader2 size={15} className={styles.spin} /> : <Search size={15} />}
                                    <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by mobile number or name" autoFocus />
                                    {canAddClient && (
                                        <button type="button" className={styles.ghostBtn} onClick={() => setShowAddClient(true)}>
                                            <UserPlus size={14} /> New client
                                        </button>
                                    )}
                                </div>
                                {query.trim().length >= 2 && !isSearching && (
                                    <div className={styles.results}>
                                        {results.length ? results.map((c) => (
                                            <button type="button" key={c._id || c.id || c.phone} className={styles.resultRow} onClick={() => pickClient(c)}>
                                                <span className={styles.avatar}>{String(c.name || '?').charAt(0).toUpperCase()}</span>
                                                <span className={styles.resultText}>
                                                    <strong>{c.name}</strong>
                                                    <small>+91 {c.phone}</small>
                                                </span>
                                            </button>
                                        )) : <div className={styles.noResult}>No client found for "{query.trim()}".</div>}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className={styles.clientRow}>
                                    <span className={styles.avatarLg}>{String(client.name || '?').charAt(0).toUpperCase()}</span>
                                    <div className={styles.clientText}>
                                        <strong>{client.name}</strong>
                                        <span>+91 {client.phone}</span>
                                    </div>
                                    {Number(client.walletBalance) > 0 && <span className={styles.walletChip}><Wallet size={12} /> Khata {money(client.walletBalance)}</span>}
                                    {canPassbook && (
                                        <button type="button" className={styles.linkBtn} onClick={() => openHistory(`${client.name}: all membership history`, { customerId: client._id || client.id })}>
                                            <History size={13} /> History
                                        </button>
                                    )}
                                    <button type="button" className={styles.linkBtn} onClick={() => { setClient(null); setWallet({ cards: [], pending: [], discounts: [], packages: [] }); resetOrder(); }}>Change</button>
                                </div>

                                {walletState.loading ? (
                                    <div className={styles.muted} style={{ marginTop: 16 }}><Loader2 size={15} className={styles.spin} /> Loading memberships...</div>
                                ) : walletState.error ? (
                                    <div className={styles.notice} style={{ marginTop: 16 }}>
                                        <AlertCircle size={15} />
                                        <div>
                                            <strong>Memberships could not be loaded</strong>
                                            <span>{walletState.error}</span>
                                            <div className={styles.noticeActions}><button type="button" onClick={() => loadWallet(client._id || client.id)}>Try again</button></div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.wallet}>
                                        {cards.map((c) => (
                                            <div key={c.id} className={`${styles.prepaidCard} ${c.status !== 'ASSIGNED' ? styles.cardMuted : ''}`}>
                                                <div className={styles.pcTop}>
                                                    <span>{c.name}</span>
                                                    <span className={styles.pcStatus}>{STATUS_LABEL[c.status] || c.status}</span>
                                                </div>
                                                <div className={styles.pcBalance}>{money(c.balance)}</div>
                                                <div className={styles.pcBottom}>
                                                    <span>•••• {lastFour(c.number)}</span>
                                                    <span>Till {fmtDate(c.expiryDate)}</span>
                                                </div>
                                                {canPassbook && (
                                                    <button type="button" className={styles.pcHistory} onClick={() => openHistory(`Card ending ${lastFour(c.number)}`, { cardId: c.id })}>
                                                        <History size={12} /> Passbook
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        {discounts.map((d) => (
                                            <div key={d.id} className={`${styles.discountCard} ${d.status !== 'ACTIVE' ? styles.cardMuted : ''}`}>
                                                <div className={styles.dcTop}><Crown size={13} /> <span>{d.name}</span></div>
                                                <div className={styles.dcBig}>{d.serviceDiscount}%<small> off services</small></div>
                                                <div className={styles.dcBottom}>
                                                    <span className={styles.dcStatus} data-status={d.status}>{STATUS_LABEL[d.status] || d.status}</span>
                                                    <span>Till {fmtDate(d.expiryDate)}</span>
                                                </div>
                                                <div className={styles.tileActions}>
                                                    {d.status === 'INACTIVE' && canActivate && (
                                                        <button type="button" className={styles.tileBtnSolid} onClick={() => activateDiscount(d.id)} disabled={Boolean(busyId)}>
                                                            {busyId === d.id ? <Loader2 size={12} className={styles.spin} /> : <Power size={12} />} Activate
                                                        </button>
                                                    )}
                                                    {canPassbook && (
                                                        <button type="button" className={styles.dcHistory} onClick={() => openHistory(d.name, { membershipId: d.id })}>
                                                            <History size={12} /> History
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        {pending.map((p) => {
                                            const allot = allotOpen[p.id];
                                            return (
                                                <div key={p.id} className={styles.pendingCard}>
                                                    <Clock size={14} />
                                                    <div>
                                                        <strong>{p.name}</strong>
                                                        <span>Paid on {fmtDate(p.purchaseDate)}. Card not allotted yet.</span>
                                                        {!canAllot ? (
                                                            <span className={styles.muted}><Lock size={11} /> Ask a manager to allot the card.</span>
                                                        ) : !allot ? (
                                                            <button type="button" className={styles.tileBtnSolid} onClick={() => openAllot(p)} disabled={Boolean(busyId)}>
                                                                <CreditCard size={12} /> Allot card
                                                            </button>
                                                        ) : allot.loading ? (
                                                            <span className={styles.muted}><Loader2 size={12} className={styles.spin} /> Loading cards...</span>
                                                        ) : allot.cards.length === 0 ? (
                                                            <span>No free cards of this plan. Generate more cards first.</span>
                                                        ) : (
                                                            <div className={styles.allotRow}>
                                                                <select
                                                                    value={allot.selected}
                                                                    onChange={(e) => setAllotOpen((m) => ({ ...m, [p.id]: { ...m[p.id], selected: e.target.value } }))}
                                                                    aria-label="Card to allot"
                                                                >
                                                                    {allot.cards.map((c) => (
                                                                        <option key={c.cardNumber} value={c.cardNumber}>
                                                                            {String(c.cardNumber).replace(/\s+/g, '').match(/.{1,4}/g)?.join(' ')}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <button type="button" className={styles.tileBtnSolid} onClick={() => allotCard(p.id)} disabled={busyId === p.id}>
                                                                    {busyId === p.id ? <Loader2 size={12} className={styles.spin} /> : <Check size={12} />} Allot
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {packages.map((pkg) => {
                                            const live = pkg.status === 'ACTIVE';
                                            const open = openPackage === pkg.id;
                                            return (
                                                <div key={pkg.id} className={`${styles.discountCard} ${live ? '' : styles.cardMuted}`}>
                                                    <div className={styles.dcTop}><PackageOpen size={13} /> <span>{pkg.name}</span></div>
                                                    <div className={styles.dcBig}>{pkg.totalLeft}<small> of {pkg.totalQty} left</small></div>
                                                    <div className={styles.dcBottom}>
                                                        <span className={styles.dcStatus} data-status={live ? 'ACTIVE' : 'EXPIRED'}>{live ? 'Active' : pkg.status === 'EXPIRED' ? 'Expired' : 'Used up'}</span>
                                                        <span>Till {fmtDate(pkg.expiryDate)}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, margin: '8px 0 2px' }}>
                                                        {pkg.services.map((svc) => (
                                                            <div key={svc.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5, fontWeight: 700 }}>
                                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{svc.name}</span>
                                                                <span style={{ color: svc.left > 0 ? 'var(--h-color-success-600, #007a49)' : 'var(--h-color-neutral-400, #9d9ea4)', fontWeight: 900 }}>
                                                                    {svc.left}/{svc.total}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className={styles.tileActions}>
                                                        {canBuyPackage && pkg.templateId && (
                                                            <button type="button" className={styles.tileBtnSolid} onClick={() => buyPackageAgain(pkg)}>
                                                                <ShoppingBag size={12} /> Buy again
                                                            </button>
                                                        )}
                                                        {pkg.history.length > 0 && (
                                                            <button type="button" className={styles.dcHistory} onClick={() => setOpenPackage(open ? '' : pkg.id)} aria-expanded={open}>
                                                                <History size={12} /> {open ? 'Hide' : 'History'} {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                                            </button>
                                                        )}
                                                    </div>
                                                    {open && (
                                                        <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            {pkg.history.map((h, i) => (
                                                                <li key={`${h.invoiceId}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11, fontWeight: 600 }}>
                                                                    <span>
                                                                        {h.serviceName} ×{h.qty}{h.staff?.length ? ` (${h.staff.join(', ')})` : ''}
                                                                        <small style={{ display: 'block', opacity: 0.7 }}>{fmtDateTime(h.at)}</small>
                                                                    </span>
                                                                    {h.invoiceId ? (
                                                                        <button type="button" className={styles.linkBtn} onClick={() => navigate(`/create-bill/${h.invoiceId}/preview`)}>Bill #{h.invoiceNo}</button>
                                                                    ) : <span>#{h.invoiceNo}</span>}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {finishedPackages.length > 0 && (
                                            <button type="button" className={styles.linkBtn} onClick={() => setShowFinished((v) => !v)} style={{ alignSelf: 'center' }}>
                                                {showFinished ? 'Hide finished packages' : `Show finished packages (${finishedPackages.length})`}
                                            </button>
                                        )}
                                        {!cards.length && !discounts.length && !pending.length && !packages.length && !finishedPackages.length && (
                                            <div className={styles.emptyWallet}>{canSell ? 'No memberships yet. Sell one below.' : 'No memberships yet.'}</div>
                                        )}
                                    </div>
                                )}
                                {flash && (
                                    <div className={flash.type === 'ok' ? styles.flashOk : styles.errorLine} role="status" style={{ marginTop: 12 }}>
                                        {flash.type === 'ok' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {flash.text}
                                    </div>
                                )}
                            </>
                        )}
                    </section>

                    {/* STEP 2: action */}
                    <section className={`${styles.step} ${!client ? styles.stepLocked : ''}`}>
                        <div className={styles.stepHead}>
                            <span className={`${styles.stepNo} ${action ? styles.stepDone : ''}`}>{action ? <Check size={13} /> : 2}</span>
                            <h2>What do you want to do?</h2>
                        </div>
                        {visibleActions.length === 0 ? (
                            <div className={styles.muted}><Lock size={14} /> You cannot sell or renew memberships. Ask your manager for access.</div>
                        ) : (
                            <div className={styles.actionGrid}>
                                {visibleActions.map(({ key, label, hint, Icon }) => {
                                    const reason = actionDisabledReason(key);
                                    return (
                                        <button
                                            type="button"
                                            key={key}
                                            className={`${styles.actionTile} ${action === key ? styles.actionActive : ''}`}
                                            disabled={Boolean(reason)}
                                            onClick={() => { resetOrder(); setAction(key); }}
                                        >
                                            <Icon size={18} />
                                            <strong>{label}</strong>
                                            <span>{reason || hint}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    {/* STEP 3: which membership */}
                    {needsTarget && (
                        <section className={styles.step}>
                            <div className={styles.stepHead}>
                                <span className={`${styles.stepNo} ${target ? styles.stepDone : ''}`}>{target ? <Check size={13} /> : 3}</span>
                                <h2>{action === 'RECHARGE' ? 'Which card?' : 'Which membership?'}</h2>
                            </div>
                            <div className={styles.targetList}>
                                {targets.map((t) => (
                                    <button
                                        type="button"
                                        key={t.id}
                                        className={`${styles.targetRow} ${targetId === t.id ? styles.targetActive : ''}`}
                                        onClick={() => { setTargetId(t.id); setPlanId(''); }}
                                        aria-pressed={targetId === t.id}
                                    >
                                        <span className={styles.radio} />
                                        <span className={styles.targetIcon}>{t.kind === 'PREPAID' ? <CreditCard size={15} /> : <Crown size={15} />}</span>
                                        <span className={styles.targetText}>
                                            <strong>{t.name}{t.kind === 'PREPAID' ? ` (•••• ${lastFour(t.number)})` : ''}</strong>
                                            <small>
                                                {t.kind === 'PREPAID' ? `Balance ${money(t.balance)}, ` : `${t.serviceDiscount}% off, `}
                                                {STATUS_LABEL[t.status] || t.status}, till {fmtDate(t.expiryDate)}
                                            </small>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* STEP 4: plan */}
                    {action && (!needsTarget || target) && (
                        <section className={styles.step}>
                            <div className={styles.stepHead}>
                                <span className={`${styles.stepNo} ${plan ? styles.stepDone : ''}`}>{plan ? <Check size={13} /> : needsTarget ? 4 : 3}</span>
                                <h2>{action === 'UPGRADE' ? 'Upgrade to' : action === 'RECHARGE' ? 'Recharge amount' : 'Choose plan'}</h2>
                            </div>

                            {action === 'SELL' && (
                                <div className={styles.segment} role="tablist">
                                    <button type="button" role="tab" aria-selected={sellKind === 'PREPAID'} className={sellKind === 'PREPAID' ? styles.segActive : ''} onClick={() => { setSellKind('PREPAID'); setPlanId(''); }}>
                                        <CreditCard size={14} /> Prepaid card
                                    </button>
                                    <button type="button" role="tab" aria-selected={sellKind === 'DISCOUNT'} className={sellKind === 'DISCOUNT' ? styles.segActive : ''} onClick={() => { setSellKind('DISCOUNT'); setPlanId(''); }}>
                                        <Crown size={14} /> Discount membership
                                    </button>
                                </div>
                            )}

                            {sellDiscountBlocked ? (
                                <div className={styles.notice}>
                                    <AlertCircle size={15} />
                                    <div>
                                        {activeDiscount ? (
                                            <>
                                                <strong>{client.name} already has {activeDiscount.name}.</strong>
                                                <span>A client can have one discount membership at a time.</span>
                                                <div className={styles.noticeActions}>
                                                    {!actionDisabledReason('UPGRADE') && <button type="button" onClick={() => { resetOrder(); setAction('UPGRADE'); setTargetId(activeDiscount.id); }}>Upgrade it</button>}
                                                    {!actionDisabledReason('RENEW') && <button type="button" onClick={() => { resetOrder(); setAction('RENEW'); setTargetId(activeDiscount.id); }}>Extend it</button>}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <strong>{waitingDiscount.name} is bought but not activated yet.</strong>
                                                <span>{canActivate ? 'Activate it above instead of selling a new one.' : 'Ask a manager to activate it instead of selling a new one.'}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : plansState.loading ? (
                                <div className={styles.muted}><Loader2 size={15} className={styles.spin} /> Loading plans...</div>
                            ) : plans.length === 0 ? (
                                <div className={styles.muted}>
                                    <AlertCircle size={15} />
                                    {plansState.error || (action === 'RENEW' ? 'This plan is archived. Use Upgrade or sell a new plan.' : 'No plans available. Add plans in Memberships settings.')}
                                </div>
                            ) : (
                                <div className={styles.planGrid}>
                                    {plans.map((p) => (
                                        <button
                                            type="button"
                                            key={p.id}
                                            className={`${styles.planTile} ${planId === p.id ? styles.planActive : ''}`}
                                            onClick={() => setPlanId(p.id)}
                                            aria-pressed={planId === p.id}
                                        >
                                            {planId === p.id && <span className={styles.planCheck}><Check size={12} /></span>}
                                            <span className={styles.planName}>{p.name}</span>
                                            {p.kind === 'PREPAID' ? (
                                                <>
                                                    <span className={styles.planHero}>Get {money(p.credit)}</span>
                                                    {p.credit > p.price && <span className={styles.planBonus}>{money(p.credit - p.price)} extra</span>}
                                                </>
                                            ) : (
                                                <span className={styles.planHero}>{p.serviceDiscount}% off</span>
                                            )}
                                            <span className={styles.planFoot}>
                                                <span>{validityText(p.validity, p.validityType) || 'No expiry'}</span>
                                                <strong>{money(p.price)}</strong>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}
                </div>

                {/* ======================= RIGHT: summary ======================= */}
                <aside className={styles.summary}>
                    {doneOrder ? (
                        <div className={styles.done}>
                            <span className={styles.doneIcon}><Check size={22} /></span>
                            <h3>{doneOrder.actionLabel} done</h3>
                            <p>{doneOrder.planName} for {doneOrder.clientName}. {money(doneOrder.total)} received via {doneOrder.paymentMode}.</p>
                            <p className={styles.doneBill}>Bill #{doneOrder.invoiceNo}</p>
                            {doneOrder.pendingAllotment && (
                                <p className={styles.doneNote}>{canAllot ? 'Allot a physical card to activate the balance.' : 'A manager needs to allot a physical card to activate the balance.'}</p>
                            )}
                            <div className={styles.doneActions}>
                                {doneOrder.invoiceId && (
                                    <button type="button" className={styles.checkoutBtn} onClick={() => navigate(`/create-bill/${doneOrder.invoiceId}/preview`)}>
                                        View and share bill
                                    </button>
                                )}
                                {doneOrder.pendingAllotment && canAllot && (
                                    <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/card-allotment')}>Allot card now</button>
                                )}
                                <button type="button" className={styles.secondaryBtn} onClick={resetOrder}>New order for {client?.name}</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <h3 className={styles.summaryTitle}>Order summary</h3>

                            {!readyForQuote ? (
                                <div className={styles.summaryEmpty}>
                                    <p>{!client ? 'Select a client to begin.' : !action ? 'Choose what you want to do.' : needsTarget && !target ? 'Choose the membership.' : sellDiscountBlocked ? 'This client cannot buy another discount membership.' : 'Choose a plan.'}</p>
                                </div>
                            ) : quoteState.loading && !quote ? (
                                <div className={styles.summaryEmpty}><p><Loader2 size={15} className={styles.spin} /> Calculating price...</p></div>
                            ) : quoteState.error ? (
                                <div className={styles.notice}><AlertCircle size={15} /><div><strong>Cannot do this</strong><span>{quoteState.error}</span></div></div>
                            ) : quote && (
                                <>
                                    <div className={styles.summaryHead}>
                                        <span className={styles.summaryAction}>{actionLabel}</span>
                                        <strong>{quote.plan.name}</strong>
                                        <span>{client?.name}</span>
                                    </div>

                                    <div className={styles.compare}>
                                        {quote.preview?.balanceBefore !== undefined && (
                                            <div className={styles.compareRow}>
                                                <span className={styles.compareLabel}>Card balance</span>
                                                <span className={styles.compareValues}><s>{money(quote.preview.balanceBefore)}</s><strong>{money(quote.preview.balanceAfter)}</strong></span>
                                            </div>
                                        )}
                                        {action === 'SELL' && quote.membershipType === 'PREPAID' && (
                                            <div className={styles.compareRow}>
                                                <span className={styles.compareLabel}>Card balance</span>
                                                <span className={styles.compareValues}><strong>{money(quote.plan.credit)}</strong></span>
                                            </div>
                                        )}
                                        {quote.fromPlan && (
                                            <div className={styles.compareRow}>
                                                <span className={styles.compareLabel}>Plan</span>
                                                <span className={styles.compareValues}><s>{quote.fromPlan.name}</s><strong>{quote.plan.name}</strong></span>
                                            </div>
                                        )}
                                        {quote.membershipType === 'DISCOUNT' && (
                                            <div className={styles.compareRow}>
                                                <span className={styles.compareLabel}>Discount</span>
                                                <span className={styles.compareValues}><strong>{quote.plan.serviceDiscount}% off services</strong></span>
                                            </div>
                                        )}
                                        {quote.preview?.expiryAfter && (
                                            <div className={styles.compareRow}>
                                                <span className={styles.compareLabel}>Valid till</span>
                                                <span className={styles.compareValues}>
                                                    {quote.preview.expiryBefore && <s>{fmtDate(quote.preview.expiryBefore)}</s>}
                                                    <strong>{fmtDate(quote.preview.expiryAfter)}</strong>
                                                </span>
                                            </div>
                                        )}
                                        {quote.preview?.note && <p className={styles.compareNote}>{quote.preview.note}</p>}
                                        {quote.preview?.extend && <p className={styles.compareNote}>Extra validity is added after the current end date.</p>}
                                    </div>

                                    <div className={styles.priceLines}>
                                        <div><span>Plan price</span><span>{money(quote.planPrice)}</span></div>
                                        {quote.upgradeCredit > 0 && <div className={styles.credit}><span>Unused value of {quote.fromPlan?.name}</span><span>- {money(quote.upgradeCredit)}</span></div>}
                                        {quote.gst > 0 && <div><span>GST ({quote.gstRate}%)</span><span>{money(quote.gst)}</span></div>}
                                        {quote.roundOff !== 0 && <div><span>Round off</span><span>{money(quote.roundOff)}</span></div>}
                                    </div>

                                    {quote.gstAllowed ? (
                                        <div className={styles.gstBox}>
                                            <label className={styles.gstToggle}>
                                                <input type="checkbox" checked={gstOn} onChange={(e) => setGstOn(e.target.checked)} />
                                                <span>Add GST</span>
                                            </label>
                                            {gstOn && (
                                                <div className={styles.gstRates} role="radiogroup" aria-label="GST rate">
                                                    {(quote.gstOptions || [5, 12, 18, 28]).filter((r) => r > 0).map((r) => {
                                                        const current = gstRate ?? quote.defaultGstRate;
                                                        return (
                                                            <button type="button" key={r} role="radio" aria-checked={current === r} className={current === r ? styles.gstActive : ''} onClick={() => setGstRate(r)}>
                                                                {r}%
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className={styles.compareNote}>No GST: this store does not issue GST bills.</p>
                                    )}

                                    <div className={styles.payTotal}>
                                        <span>To pay</span>
                                        <strong>{money(quote.total)}</strong>
                                    </div>

                                    {staffList.length > 0 && (
                                        <label className={styles.soldBy}>
                                            <span>Sold by</span>
                                            <select value={soldById} onChange={(e) => setSoldById(e.target.value)}>
                                                <option value="">Select staff (optional)</option>
                                                {staffList.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                                            </select>
                                        </label>
                                    )}

                                    <div className={styles.payModes} role="radiogroup" aria-label="Payment mode">
                                        {PAYMENT_MODES.map(({ key, label, Icon }) => (
                                            <button type="button" key={key} role="radio" aria-checked={paymentMode === key} className={paymentMode === key ? styles.payActive : ''} onClick={() => setPaymentMode(key)}>
                                                <Icon size={14} /> {label}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}

                            {checkoutError && <div className={styles.errorLine}><AlertCircle size={14} /> {checkoutError}</div>}

                            <button type="button" className={styles.checkoutBtn} disabled={!quote || quoteState.loading || isSubmitting} onClick={handleCheckout}>
                                {isSubmitting && <Loader2 size={16} className={styles.spin} />}
                                {quote ? `Collect ${money(quote.total)}` : 'Collect payment'}
                            </button>
                            {client && (
                                <button type="button" className={styles.clearBtn} onClick={resetOrder} disabled={!action}>
                                    <X size={13} /> Clear selection
                                </button>
                            )}
                        </>
                    )}
                </aside>
            </div>

            {/* ======================= HISTORY DRAWER ======================= */}
            {historyFor && canPassbook && (
                <div className={styles.drawerBackdrop} onClick={() => setHistoryFor(null)} role="presentation">
                    <aside className={styles.drawer} onClick={(e) => e.stopPropagation()} aria-label="Membership history">
                        <div className={styles.drawerHead}>
                            <div>
                                <h3>Passbook</h3>
                                <p>{historyFor.title}</p>
                            </div>
                            <button type="button" className={styles.backBtn} onClick={() => setHistoryFor(null)} aria-label="Close"><X size={16} /></button>
                        </div>

                        <div className={styles.drawerBody}>
                            {historyState.error && <div className={styles.errorLine}><AlertCircle size={14} /> {historyState.error}</div>}
                            {!historyState.loading && !historyState.error && historyRows.length === 0 && (
                                <div className={styles.summaryEmpty}><p>No history yet.</p></div>
                            )}

                            {historyRows.map((row) => {
                                const isCredit = Number(row.credit) > 0;
                                const isDebit = Number(row.debit) > 0;
                                return (
                                    <div key={row._id} className={styles.txn}>
                                        <div className={styles.txnTop}>
                                            <div>
                                                <strong>{TXN_LABEL[row.type] || row.type}</strong>
                                                <span>{fmtDateTime(row.createdAt)}</span>
                                            </div>
                                            {isCredit && <span className={styles.txnPlus}>+ {money(row.credit)}</span>}
                                            {isDebit && <span className={styles.txnMinus}>- {money(row.debit)}</span>}
                                            {!isCredit && !isDebit && row.amountPaid > 0 && <span className={styles.txnPaid}>Paid {money(row.amountPaid)}</span>}
                                        </div>

                                        {row.usage?.length > 0 && (
                                            <ul className={styles.usage}>
                                                {row.usage.map((u, i) => (
                                                    <li key={`${row._id}-${i}`}>
                                                        <span>{u.itemName}{u.staff?.length ? ` (${u.staff.join(', ')})` : ''}</span>
                                                        <span>{money(u.amount)}{u.amount < u.itemAmount ? ` of ${money(u.itemAmount)}` : ''}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        <div className={styles.txnMeta}>
                                            {row.balanceAfter !== null && row.balanceAfter !== undefined && <span>Balance {money(row.balanceAfter)}</span>}
                                            {row.fromPlan?.name && <span>{row.fromPlan.name} → {row.plan?.name}</span>}
                                            {row.upgradeCredit > 0 && <span>Old plan credit {money(row.upgradeCredit)}</span>}
                                            {row.expiryAfter && <span>Valid till {fmtDate(row.expiryAfter)}</span>}
                                            {row.cardNumber && !historyFor.params.cardId && <span>Card •••• {lastFour(row.cardNumber)}</span>}
                                            {row.invoiceNo && row.invoiceId && (
                                                <button type="button" className={styles.linkBtn} onClick={() => navigate(`/create-bill/${row.invoiceId}/preview`)}>Bill #{row.invoiceNo}</button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            {historyState.loading && <div className={styles.muted}><Loader2 size={15} className={styles.spin} /> Loading...</div>}
                            {!historyState.loading && historyState.nextBefore && (
                                <button type="button" className={styles.secondaryBtn} onClick={() => openHistory(historyFor.title, historyFor.params, true)}>Load older</button>
                            )}
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}